import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { ReactElement, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  clearCourseMigrationStatus,
  fetchCourseMigratableContent,
  fetchCourseV2Compliances,
  fetchCourseV2CAPCEAccreditation,
  fetchCourseV2ANCCAccreditation,
  migrateCourseV2Accreditation,
  selectCourseMigrationError,
  selectCourseMigrationStatus,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'

type Props = {
  isOpen: boolean
  onClose: () => void
  courseID: string
  hasCapce: boolean
  hasAncc: boolean
}

export default function MigrateAccreditationModal({
  isOpen,
  onClose,
  courseID,
  hasCapce,
  hasAncc,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let migrationStatus = useAppSelector(selectCourseMigrationStatus)
  let migrationError = useAppSelector(selectCourseMigrationError)

  let isLoading = migrationStatus === 'pending'

  let accreditationTypes = [hasCapce && 'CAPCE', hasAncc && 'ANCC']
    .filter(Boolean)
    .join(' and ')

  useEffect(() => {
    if (migrationStatus === 'fulfilled') {
      dispatch(fetchCourseMigratableContent(courseID))
      dispatch(fetchCourseV2Compliances())
      if (hasCapce) dispatch(fetchCourseV2CAPCEAccreditation(courseID))
      if (hasAncc) dispatch(fetchCourseV2ANCCAccreditation(courseID))
      dispatch(clearCourseMigrationStatus())
      onClose()
    }
  }, [migrationStatus, courseID, hasCapce, hasAncc, dispatch, onClose])

  let handleClose = () => {
    dispatch(clearCourseMigrationStatus())
    onClose()
  }

  let handleConfirm = () => {
    dispatch(migrateCourseV2Accreditation(courseID))
  }

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: modalPaperProps }}
    >
      <DialogTitle>Migrate Accreditation to v2</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          This will migrate {accreditationTypes} accreditation records to v2 for
          this course.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Migrated fields will be set to <strong>Updated</strong> and will
          require compliance review. Some fields will be empty and need to be
          filled in after migration.
        </Typography>
        {migrationError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {migrationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? 'Migrating…' : 'Migrate Accreditation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
